/**
 * Draws the fishing bobber (entities/bobber.ts): the 8x8 `fishing_hook` sprite as a camera-facing quad, the
 * thin line from the rod tip with vanilla's sag (16 segments, y = dy * (t^2 + t) / 2), and the small particles
 * the sim reports (splash on entering water, wake while the fish closes in, bubbles on the bite).
 */

import * as THREE from 'three';
import type { Bobber, BobberFx } from '../entities/bobber';

const POOL = 96;
const SEGMENTS = 16;

interface Puff { s: THREE.Sprite; life: number; vx: number; vy: number; vz: number; g: number }

export class BobberRenderer {
  readonly group = new THREE.Group();
  private readonly hookSprite: THREE.Sprite;
  private readonly line: THREE.Line;
  private readonly linePos: Float32Array;
  private readonly puffs: Puff[] = [];
  private next = 0;
  private readonly mats: Record<'splash' | 'bubble', THREE.SpriteMaterial>;

  constructor() {
    const load = (url: string): THREE.Texture => {
      const t = new THREE.TextureLoader().load(url);
      t.magFilter = THREE.NearestFilter; t.minFilter = THREE.NearestFilter; t.colorSpace = THREE.SRGBColorSpace;
      return t;
    };
    this.hookSprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: load('mc/entity/fishing/fishing_hook.png'), transparent: true, alphaTest: 0.1 }));
    this.hookSprite.scale.setScalar(0.5);
    this.hookSprite.visible = false;
    this.group.add(this.hookSprite);

    this.linePos = new Float32Array((SEGMENTS + 1) * 3);
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(this.linePos, 3));
    this.line = new THREE.Line(g, new THREE.LineBasicMaterial({ color: 0x1a1a1a }));
    this.line.frustumCulled = false;
    this.line.visible = false;
    this.group.add(this.line);

    this.mats = {
      splash: new THREE.SpriteMaterial({ map: load('mc/particle/splash_0.png'), transparent: true, alphaTest: 0.1, depthWrite: false }),
      bubble: new THREE.SpriteMaterial({ map: load('mc/particle/bubble.png'), transparent: true, alphaTest: 0.1, depthWrite: false }),
    };
    for (let i = 0; i < POOL; i++) {
      const s = new THREE.Sprite(this.mats.splash);
      s.visible = false;
      this.group.add(s);
      this.puffs.push({ s, life: 0, vx: 0, vy: 0, vz: 0, g: 0 });
    }
  }

  private puff(mat: 'splash' | 'bubble', x: number, y: number, z: number, vx: number, vy: number, vz: number, size: number, life: number, g: number): void {
    const p = this.puffs[this.next];
    this.next = (this.next + 1) % POOL;
    p.s.material = this.mats[mat];
    p.s.position.set(x, y, z);
    p.s.scale.setScalar(size);
    p.s.visible = true;
    p.life = life; p.vx = vx; p.vy = vy; p.vz = vz; p.g = g;
  }

  /** Sim event -> particles. */
  fx(kind: BobberFx, x: number, y: number, z: number): void {
    const r = (): number => Math.random() - 0.5;
    if (kind === 'splash' || kind === 'catch') {
      for (let i = 0; i < 10; i++) this.puff('splash', x + r() * 0.3, y, z + r() * 0.3, r() * 1.2, 1.2 + Math.random() * 1.4, r() * 1.2, 0.12, 0.5, 9);
    } else if (kind === 'bite') {
      for (let i = 0; i < 10; i++) this.puff('splash', x + r() * 0.4, y, z + r() * 0.4, r() * 1.5, 1.5 + Math.random() * 1.5, r() * 1.5, 0.14, 0.55, 9);
      for (let i = 0; i < 6; i++) this.puff('bubble', x + r() * 0.4, y - 0.1, z + r() * 0.4, r() * 0.3, 0.5 + Math.random() * 0.6, r() * 0.3, 0.1, 0.7, 0);
    } else if (kind === 'wake') {
      this.puff('splash', x, y + 0.02, z, 0, 0.1, 0, 0.14, 0.35, 0);
    }
  }

  /**
   * `tip` is the rod tip in world space; `hook` null hides everything. `t` (seconds) drives the line's tiny sway.
   */
  update(hook: Bobber | null, alpha: number, dt: number, tip: THREE.Vector3): void {
    for (const p of this.puffs) {
      if (p.life <= 0) continue;
      p.life -= dt;
      if (p.life <= 0) { p.s.visible = false; continue; }
      p.vy -= p.g * dt;
      p.s.position.x += p.vx * dt; p.s.position.y += p.vy * dt; p.s.position.z += p.vz * dt;
    }
    if (!hook) { this.hookSprite.visible = false; this.line.visible = false; return; }
    const x = hook.px + (hook.x - hook.px) * alpha, y = hook.py + (hook.y - hook.py) * alpha, z = hook.pz + (hook.z - hook.pz) * alpha;
    this.hookSprite.visible = true;
    this.hookSprite.position.set(x, y + 0.125, z);
    const dx = x - tip.x, dy = y + 0.25 - tip.y, dz = z - tip.z;
    const L = this.linePos;
    for (let i = 0; i <= SEGMENTS; i++) {
      const f = i / SEGMENTS;
      L[i * 3] = tip.x + dx * f;
      L[i * 3 + 1] = tip.y + dy * (f * f + f) * 0.5;
      L[i * 3 + 2] = tip.z + dz * f;
    }
    (this.line.geometry.attributes.position as THREE.BufferAttribute).needsUpdate = true;
    this.line.visible = true;
  }
}
